import { useState } from "react";

export default function MetadataBuilder({
  value = [],
  onChange,
  errors = {},
  expectedSamples,
  onLockChange,
  columns = [
    {
      key: "sample_id",
      label: "Sample ID",
    },
  ],
}) {
  const metadata = value;

  const [locked, setLocked] = useState(false);
  const [metadataError, setMetadataError] = useState("");

  const updateSample = (index, sampleId) => {
    const updated = [...metadata];
    updated[index].sample_id = sampleId;
    onChange(updated);
  };

  const addSample = () => {
    const newRow = {};

    columns.forEach((column) => {
      newRow[column.key] = "";
    });

    onChange([
      ...metadata,
      newRow,
    ]);
  };

  const removeSample = (index) => {
    onChange(metadata.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    if (metadata.length === 0) {
      setMetadataError("At least one sample is required.");
      return;
    }

    const expected = Number(expectedSamples || 0);

    if (expected > 0 && metadata.length !== expected) {
      setMetadataError(
        `You have specified ${expected} sample${expected > 1 ? "s" : ""}, but ${metadata.length} metadata row${metadata.length !== 1 ? "s have" : " has"} been added.`
      );
      return;
    }

    const sampleIds = new Set();

    for (const row of metadata) {
      const sampleId = row.sample_id.trim();

      if (!sampleId) {
        setMetadataError("Please enter Sample ID for all samples.");
        return;
      }

      if (sampleIds.has(sampleId.toLowerCase())) {
        setMetadataError("Duplicate Sample IDs are not allowed.");
        return;
      }

      sampleIds.add(sampleId.toLowerCase());
    }

    for (const row of metadata) {
      for (const column of columns) {
        if (
          column.required &&
          !column.required(row)
        ) {
          setMetadataError(
            column.requiredMessage ||
            `${column.label} is required.`
          );
          return;
        }
      }
    }


    const updatedMetadata = metadata.map((row) => {
      const updatedRow = { ...row };

      // Only normalize condition if this module actually has a condition field
      if (Object.prototype.hasOwnProperty.call(updatedRow, "condition")) {
        updatedRow.condition = updatedRow.condition
          ? updatedRow.condition.trim().toUpperCase()
          : "";
      }

      return updatedRow;
    });

    onChange(updatedMetadata);

    setMetadataError("");
    setLocked(true);
    onLockChange?.(true);
  };

  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-800">
        Metadata Builder
      </h3>

      <div className="mt-4 overflow-x-auto">
        <table className="w-full border border-gray-200 rounded-lg overflow-hidden">
          <thead className="bg-gray-50">
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  className="border px-3 py-2 text-left text-xs font-semibold text-gray-700"
                >
                  {column.label}
                </th>
              ))}

              <th className="border px-3 py-2 text-center text-xs font-semibold text-gray-700 w-32">
                Action
              </th>
            </tr>
          </thead>

          <tbody>
            {metadata.map((sample, index) => (
              <tr key={index}>
                {columns.map((column) => (
                  <td key={column.key} className="border p-2">
                    {column.type === "select" ? (
                      <select
                        value={sample[column.key] || ""}
                        disabled={
                          locked ||
                          (column.disabled?.(sample) ?? false)
                        }
                        onChange={(e) => {
                          const updated = [...metadata];

                          updated[index][column.key] = e.target.value;

                          column.onChange?.(
                            updated[index],
                            e.target.value,
                            updated,
                            index
                          );

                          onChange(updated);
                        }}
                        className="w-full rounded border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-100"
                      >
                        <option value="">Select</option>

                        {column.options?.map((option) => (
                          <option
                            key={option}
                            value={option}
                            disabled={
                              column.optionDisabled?.(
                                option,
                                metadata,
                                index
                              ) ?? false
                            }
                          >
                            {option}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        value={sample[column.key] || ""}
                        disabled={
                          locked ||
                          (column.disabled?.(sample) ?? false)
                        }
                        onChange={(e) => {
                          const updated = [...metadata];
                          updated[index][column.key] = e.target.value;
                          onChange(updated);
                        }}
                        placeholder={
                          typeof column.placeholder === "function"
                            ? column.placeholder(sample)
                            : column.placeholder || column.label
                        }
                        className="w-full rounded border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-500"
                      />
                    )}

                    {errors[`${column.key}_${index}`] && (
                      <p className="mt-1 text-xs text-red-600">
                        {errors[`${column.key}_${index}`]}
                      </p>
                    )}
                  </td>
                ))}

                <td className="border text-center">
                  {!locked && (
                    <button
                      type="button"
                      onClick={() => removeSample(index)}
                      disabled={metadata.length === 1}
                      className={`rounded px-3 py-1 text-sm ${metadata.length === 1
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "bg-red-50 text-red-600 hover:bg-red-100"
                        }`}
                    >
                      Remove
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(metadataError || errors.metadata_builder) && (
        <p className="mt-3 text-sm text-red-600">
          {metadataError || errors.metadata_builder}
        </p>
      )}

      <div className="mt-5 flex items-center justify-between">
        <button
          type="button"
          onClick={addSample}
          disabled={locked}
          className={`rounded-md px-4 py-2 text-sm font-medium text-white ${locked
            ? "bg-gray-400 cursor-not-allowed"
            : "bg-[#0B6936] hover:bg-[#09582D]"
            }`}
        >
          + Add Sample
        </button>

        <button
          type="button"
          onClick={() => {
            if (locked) {
              setLocked(false);
              setMetadataError("");
              onLockChange?.(false);
            } else {
              handleSave();
            }
          }}
          className={`rounded-md px-4 py-2 text-sm font-medium text-white ${locked
            ? "bg-amber-600 hover:bg-amber-700"
            : "bg-[#0B6936] hover:bg-[#09582D]"
            }`}
        >
          {locked ? "Modify Metadata" : "Save Metadata"}
        </button>
      </div>
    </div>
  );
}